import CustomCard from "components/Atoms/CustomCard"
import CustomDateRange from "components/Atoms/CustomDateRange"
import StaticMultiSelector from "components/Atoms/StaticMultiSelector"
import StatusIcon from "components/Atoms/StatusIcon"
import SearchFilter, {
  SearchFilterCallout,
} from "components/Organisms/SearchFilter"
import dayjs from "dayjs"

import TablePagination from "components/Molecules/TablePagination"
import CustomReactTable from "components/Templates/CustomReactTable"
import ModuleHeader, { Description } from "components/Templates/ModuleHeader"
import TimeCell from "components/Templates/table/TimeCell"
import { ORDER_STATUSES } from "data/orderStatuses"
import { ORDER_TYPES_MAP } from "data/orderTypes"
// import { PAYMENT_METHODS } from "data/static.items"
import {
  PAYMENT_SETTLEMENT_PAGE_SIZE,
  usePaymentVerificationOrders,
} from "graphql/fund-flow/fund-flow.hooks"
import React from "react"
import { useDispatch } from "react-redux"
import { useSearchParams } from "react-router-dom"
import { RESET_SELECTED } from "store/selector.redux"
import clsx from "utils/clsx"
import { arrayToMap } from "utils/custom.utils"
import { inr } from "utils/formatters.utils"
import PaymentTransactionModal from "./PaymentTransactionModal"

const SELECTOR_PATH = "payment-settlement"

const VERIFICATION_STATUSES = [
  { id: "PND", name: "Pending" },
  { id: "APR", name: "Approved" },
  { id: "REJ", name: "Rejected" },
]

const orderStatusMap = arrayToMap(ORDER_STATUSES, st => {
  const intent = /(cancel)|(reject)/gi.test(st?.name)
    ? "danger"
    : st?.id === "CMP"
    ? "success"
    : st?.id === "CNF"
    ? "warning"
    : "primary"
  return { intent, text: st?.name }
})

const paymentStatusMap = {
  SUC: { intent: "success", text: "Completed" },
  PRT: { intent: "primary", text: "Partial" },
  PND: { intent: "warning", text: "Pending" },
  INP: { intent: "primary", text: "In Process" },
  PLT: { intent: "none", text: "Pay Later" },
  FAL: { intent: "danger", text: "Failed" },
  RUF: { intent: "none", text: "Refunded" },
}

// const paymentMethodMap = arrayToMap(PAYMENT_METHODS)

const COLUMNS = [
  {
    Header: "Status",
    accessor: "orderStatus",
    Cell({ value }) {
      return (
        <div className="vt-text-center">
          <StatusIcon values={orderStatusMap} condition={value} />
        </div>
      )
    },
  },
  {
    minWidth: 145,
    Header: "Order",
    accessor: "trackingCode",
    Cell({ value, original }) {
      const [params, setParams] = useSearchParams()
      return (
        <>
          <div>
            <span
              className="vt-styled-link-text"
              onClick={() => {
                params.set("id", original?.id)
                setParams(params)
              }}
            >
              {value}
            </span>
          </div>
          <span className="vt-muted text-xs">
            {ORDER_TYPES_MAP[original?.orderCategory]?.name ??
              original?.orderCategory}
          </span>
        </>
      )
    },
  },
  {
    Header: "Business Name",
    accessor: "user.shopsManaged",
    Cell({ value }) {
      return <p className="font-400">{value?.[0]?.name || "--"}</p>
    },
  },
  {
    Header: "Placed At",
    accessor: "createdAt",
    Cell({ value }) {
      return <TimeCell timeStamp={value} />
    },
  },
  {
    Header: "Shipping Address",
    accessor: "shippingAddress.village",
    Cell({ value }) {
      return <div className="vt-text-center">{value || "--"}</div>
    },
  },
  {
    Header: "Order Amount",
    accessor: "totalAmount",
    Cell({ value, original }) {
      return (
        <div className="vt-text-center">
          <div>{inr(value)}</div>
          {/* <div className="vt-muted text-xs">
            {paymentMethodMap?.[original?.paymentMethod]?.name}
          </div> */}
        </div>
      )
    },
  },

  {
    Header: "Total Outstanding Amount",
    accessor: "outstandingAmount",
    Cell({ value }) {
      return <div className="vt-text-center vt-danger">{inr(value)}</div>
    },
  },
  {
    Header: "Payment Amount",
    id: "paymentAmount",
    accessor: original =>
      (original?.totalAmount ?? 0) - (original?.outstandingAmount ?? 0),
    Cell({ value }) {
      return (
        <div className={clsx("vt-text-center vt-success")}>{inr(value)}</div>
      )
    },
  },
  {
    Header: "Payment Status",
    accessor: "paymentStatus",
    Cell({ value }) {
      return (
        <div className="vt-text-center">
          <StatusIcon values={paymentStatusMap} condition={value} />
        </div>
      )
    },
  },
]

const PaymentSettlement = () => {
  const [params, setParams] = useSearchParams()
  const dispatch = useDispatch()
  const { data, loading } = usePaymentVerificationOrders()
  const orders = data?.paymentVerificationOrders ?? []
  const selectedOrder = orders.find(o => o?.id === params.get("id"))
  const filter = {
    verificationStatuses: VERIFICATION_STATUSES.filter(status =>
      params.getAll("verificationStatuses").includes(status.id)
    ),
    dateRange:
      params.has("startDate") || params.has("endDate")
        ? [params.get("startDate"), params.get("endDate")]
        : undefined,
  }

  const resetPage = (nextParams: URLSearchParams) => {
    nextParams.set("page", "0")
    nextParams.delete("id")
    setParams(nextParams)
    dispatch({ type: RESET_SELECTED, path: SELECTOR_PATH })
  }

  const applyFilter = value => {
    const nextParams = new URLSearchParams(params)
    nextParams.delete("verificationStatuses")
    value?.verificationStatuses?.forEach(status =>
      nextParams.append("verificationStatuses", status.id)
    )
    ;["startDate", "endDate"].forEach((key, index) => {
      const date = value?.dateRange?.[index]
      if (date && dayjs(date).isValid()) {
        nextParams.set(key, dayjs(date).format("YYYY-MM-DD"))
      } else {
        nextParams.delete(key)
      }
    })
    resetPage(nextParams)
  }

  return (
    <div className="p-3">
      <CustomCard className="vt-dispatch-table-view p-0">
        <ModuleHeader
          white
          placeholder="Search using Order Id, Business Name "
          query={params.get("search") ?? ""}
          onQueryChange={query => {
            const nextParams = new URLSearchParams(params)
            if (query) nextParams.set("search", query)
            else nextParams.delete("search")
            resetPage(nextParams)
          }}
          filter={
            <SearchFilter
              className="ml-3"
              value={filter}
              getValues={applyFilter}
              onClear={() => applyFilter({})}
            >
              <SearchFilterCallout title="Verification Status">
                <StaticMultiSelector
                  path="verificationStatuses"
                  label="Status"
                  items={VERIFICATION_STATUSES}
                  inline
                />
              </SearchFilterCallout>
              <SearchFilterCallout title="Date Range">
                <CustomDateRange path="dateRange" big={false} />
              </SearchFilterCallout>
            </SearchFilter>
          }
          borderBottom="none"
          header={
            <div className="flex items-center">
              <Description icon="credit-card" title="Payment Settlement" />
            </div>
          }
        >
          <TablePagination
            page={params.get("page") ?? 0}
            disabledNext={orders.length < PAYMENT_SETTLEMENT_PAGE_SIZE}
            onPageChange={page => {
              params.set("page", String(page))
              setParams(params)
              dispatch({ type: RESET_SELECTED, path: SELECTOR_PATH })
            }}
          />
        </ModuleHeader>
        <CustomReactTable
          pageSize={PAYMENT_SETTLEMENT_PAGE_SIZE}
          loading={loading}
          loadingText="Fetching data"
          data={orders}
          columns={COLUMNS}
        />
        <PaymentTransactionModal
          order={selectedOrder}
          isOpen={!!selectedOrder}
          onClose={() => {
            params.delete("id")
            setParams(params)
          }}
        />
      </CustomCard>
    </div>
  )
}

export default PaymentSettlement
